import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { lovable } from "@/integrations/lovable/index";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acceder | Mine Class" },
      { name: "description", content: "Panel administrativo de Mine Class (uso interno)." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function finishExistingLogin() {
      setBusy(true);
      setErr(null);
      try {
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = hash.get("access_token");
        const refreshToken = hash.get("refresh_token");

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
          if (error) throw error;
        }

        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        if (!cancelled && data.user) {
          navigate({ to: "/admin", replace: true });
        }
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "No se pudo completar el inicio de sesión.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") && session?.user) {
        navigate({ to: "/admin", replace: true });
      }
    });

    finishExistingLogin();
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  async function loginGoogle() {
    setErr(null);
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth",
        extraParams: { hd: "mine-class.cl", prompt: "select_account" },
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      navigate({ to: "/admin" });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Error inesperado.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-container-low)] px-4">
      <div className="w-full max-w-md bg-white border border-[var(--color-outline-variant)] p-8">
        <h1 className="text-2xl font-bold text-[var(--color-brand-purple)]">Panel Mine Class</h1>
        <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">
          Acceso restringido a cuentas <strong>@mine-class.cl</strong> autorizadas.
        </p>

        <button
          onClick={loginGoogle}
          disabled={busy}
          className="w-full mt-6 py-3 border border-[var(--color-outline-variant)] bg-white text-[var(--color-brand-purple)] font-bold uppercase tracking-wider hover:bg-[var(--color-surface-container-low)] disabled:opacity-50 flex items-center justify-center gap-3"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.7 26.7 35.5 24 35.5c-5.3 0-9.7-3.1-11.3-8l-6.6 5.1C9.7 39.1 16.3 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.3-.4-3.5z"/></svg>
          {busy ? "Procesando..." : "Continuar con Google"}
        </button>

        {err && <p className="text-sm text-red-600 mt-4">{err}</p>}
      </div>
    </div>
  );
}
